import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../component-styles/ReportsShared.css';
import API_BASE from '../../utils/api.js';
import { exportRowsToExcel } from '../../utils/excelExport.js';

/* --------------------------------------------------------
   A/R and A/P aging are the same report pointed at opposite
   sides of the ledger — same buckets, same rollup, same
   layout. Only the endpoint, the counterparty column and the
   document columns differ, so they share this component.
-------------------------------------------------------- */

const BUCKETS = [
  { key: 'current', label: 'Current' },
  { key: 'd1_30', label: '1-30 Days' },
  { key: 'd31_60', label: '31-60 Days' },
  { key: 'd61_90', label: '61-90 Days' },
  { key: 'd90_plus', label: '90+ Days' },
];

function formatCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function formatDate(v) {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
}

function normalizeProject(raw) {
  return {
    id: String(raw.projectId || raw.id || raw.name || ''),
    name: raw.projectNickname || raw.name || raw.title || 'Untitled Project',
  };
}

function emptyBuckets() {
  return Object.fromEntries(BUCKETS.map((b) => [b.key, 0]));
}

// Each A/R document type lives under a different project route.
const AR_DOC_PATHS = {
  Invoice: 'invoices',
  'Time Billing Invoice': 'timebilling-invoices',
  Proposal: 'proposals',
};

function documentHref(row, isAR) {
  if (!row.projectId) return null;
  if (!isAR) return row.poId ? `/projects/${row.projectId}/purchase-orders/${row.poId}` : null;
  const segment = AR_DOC_PATHS[row.docType];
  return segment && row.docId ? `/projects/${row.projectId}/${segment}/${row.docId}` : null;
}

export default function AgingReportPage({ variant }) {
  const isAR = variant === 'ar';
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');

  const config = isAR
    ? {
        title: 'A/R Aging',
        subtitle: 'Outstanding client balances by age',
        endpoint: 'ar-aging',
        partyLabel: 'Client',
        partyField: 'clientName',
        emptyText: 'No outstanding client balances.',
        totalLabel: 'Total Receivable',
        filename: 'ar-aging-report.xlsx',
        sheetName: 'AR Aging',
      }
    : {
        title: 'A/P Aging',
        subtitle: 'Outstanding vendor balances by age',
        endpoint: 'ap-aging',
        partyLabel: 'Vendor',
        partyField: 'vendorName',
        emptyText: 'No outstanding vendor balances.',
        totalLabel: 'Total Payable',
        filename: 'ap-aging-report.xlsx',
        sheetName: 'AP Aging',
      };

  const [projects, setProjects] = useState([]);
  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [asOf, setAsOf] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [projectFilter, setProjectFilter] = useState('all');
  const [bucketFilter, setBucketFilter] = useState('all');
  const [view, setView] = useState('summary');

  useEffect(() => {
    if (!userId) {
      setProjects([]);
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/${encodeURIComponent(userId)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const arr = Array.isArray(data) ? data : data?.Items || data?.items || [];
        setProjects(arr.map(normalizeProject));
      } catch (err) {
        console.error('Failed to load projects', err);
        setProjects([]);
      }
    })();
  }, [userId]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch(`${API_BASE}/api/reports/${config.endpoint}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('idToken')}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setRows(Array.isArray(data.rows) ? data.rows : []);
        setGroups(Array.isArray(data.groups) ? data.groups : []);
        setAsOf(data.asOf || '');
      } catch (err) {
        console.error(`Failed to load ${config.title}`, err);
        setError(err.message);
        setRows([]);
        setGroups([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [config.endpoint, config.title]);

  const filteredRows = useMemo(
    () =>
      rows.filter((r) => {
        if (projectFilter !== 'all' && r.projectId !== projectFilter) return false;
        if (bucketFilter !== 'all' && r.bucket !== bucketFilter) return false;
        return true;
      }),
    [rows, projectFilter, bucketFilter]
  );

  // Re-derive the rollup from the filtered rows rather than using the server's
  // groups directly, so the summary always matches whatever filters are on.
  const filteredGroups = useMemo(() => {
    if (projectFilter === 'all' && bucketFilter === 'all') return groups;

    const map = new Map();
    for (const r of filteredRows) {
      const name = r[config.partyField] || 'Unassigned';
      if (!map.has(name)) {
        map.set(name, { name, total: 0, buckets: emptyBuckets(), documentCount: 0 });
      }
      const g = map.get(name);
      g.total += r.balance;
      g.buckets[r.bucket] += r.balance;
      g.documentCount += 1;
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [groups, filteredRows, projectFilter, bucketFilter, config.partyField]);

  const totals = useMemo(() => {
    const acc = { total: 0, buckets: emptyBuckets() };
    for (const r of filteredRows) {
      acc.total += r.balance;
      acc.buckets[r.bucket] += r.balance;
    }
    return acc;
  }, [filteredRows]);

  function exportExcel() {
    const headers = isAR
      ? ['Client', 'Project', 'Type', 'Document #', 'Date', 'Due Date', 'Days Past Due', 'Total', 'Paid', 'Balance', 'Bucket']
      : ['Vendor', 'Project', 'PO #', 'Status', 'Date', 'Due Date', 'Days Past Due', 'Total', 'Paid', 'Balance', 'Bucket'];

    const bucketLabel = (key) => BUCKETS.find((b) => b.key === key)?.label || key;

    const rowsForExport = filteredRows.map((r) =>
      isAR
        ? [
            r.clientName,
            r.projectName,
            r.docType,
            r.docNumber,
            r.date ? String(r.date).slice(0, 10) : '',
            r.dueDate ? String(r.dueDate).slice(0, 10) : '',
            r.daysPastDue,
            Number(r.total || 0),
            Number(r.paid || 0),
            Number(r.balance || 0),
            bucketLabel(r.bucket),
          ]
        : [
            r.vendorName,
            r.projectName,
            r.poNumber,
            r.status,
            r.date ? String(r.date).slice(0, 10) : '',
            r.dueDate ? String(r.dueDate).slice(0, 10) : '',
            r.daysPastDue,
            Number(r.total || 0),
            Number(r.paid || 0),
            Number(r.balance || 0),
            bucketLabel(r.bucket),
          ]
    );

    exportRowsToExcel({
      filename: config.filename,
      sheetName: config.sheetName,
      headers,
      rows: rowsForExport,
    });
  }

  return (
    <div className="report-shell">
      <div className="report-shell-inner">
        <div className="report-header">
          <div className="report-title">
            <h1>{config.title}</h1>
            <div className="muted">
              {config.subtitle}
              {asOf ? ` · as of ${asOf}` : ''}
            </div>
          </div>

          <div className="report-actions">
            <button className="report-btn-secondary" onClick={() => navigate('/reports')}>
              Back
            </button>
            <button className="report-btn-primary" onClick={exportExcel}>
              Export Excel
            </button>
          </div>
        </div>

        <div className="report-filters">
          <select
            className="report-select"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            aria-label="Filter by project"
          >
            <option value="all">All Projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>

          <select
            className="report-select"
            value={bucketFilter}
            onChange={(e) => setBucketFilter(e.target.value)}
            aria-label="Filter by age"
          >
            <option value="all">All Ages</option>
            {BUCKETS.map((b) => (
              <option key={b.key} value={b.key}>
                {b.label}
              </option>
            ))}
          </select>

          <select
            className="report-select"
            value={view}
            onChange={(e) => setView(e.target.value)}
            aria-label="View"
          >
            <option value="summary">Summary by {config.partyLabel}</option>
            <option value="detail">Document Detail</option>
          </select>
        </div>

        <div className="report-card">
          <div className="report-metrics">
            <div className="report-metric">
              <div className="report-metric-value">{formatCurrency(totals.total)}</div>
              <div className="report-metric-label">{config.totalLabel}</div>
            </div>
            {BUCKETS.map((b) => (
              <div className="report-metric" key={b.key}>
                <div className="report-metric-value">{formatCurrency(totals.buckets[b.key])}</div>
                <div className="report-metric-label">{b.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="report-table-wrap">
          {loading ? (
            <div className="report-empty">Loading {config.title}...</div>
          ) : error ? (
            <div className="report-empty">Could not load this report: {error}</div>
          ) : filteredRows.length === 0 ? (
            <div className="report-empty">{config.emptyText}</div>
          ) : view === 'summary' ? (
            <table className="report-table" role="table" aria-label={`${config.title} summary`}>
              <thead>
                <tr>
                  <th>{config.partyLabel}</th>
                  <th className="right">Docs</th>
                  {BUCKETS.map((b) => (
                    <th className="right" key={b.key}>
                      {b.label}
                    </th>
                  ))}
                  <th className="right">Total</th>
                </tr>
              </thead>
              <tbody>
                {filteredGroups.map((g) => (
                  <tr key={g.name}>
                    <td>{g.name}</td>
                    <td className="right">{g.documentCount}</td>
                    {BUCKETS.map((b) => (
                      <td className="right" key={b.key}>
                        {formatCurrency(g.buckets[b.key])}
                      </td>
                    ))}
                    <td className="right">
                      <strong>{formatCurrency(g.total)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>
                    <strong>Total</strong>
                  </td>
                  <td className="right">
                    <strong>{filteredRows.length}</strong>
                  </td>
                  {BUCKETS.map((b) => (
                    <td className="right" key={b.key}>
                      <strong>{formatCurrency(totals.buckets[b.key])}</strong>
                    </td>
                  ))}
                  <td className="right">
                    <strong>{formatCurrency(totals.total)}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          ) : (
            <table className="report-table" role="table" aria-label={`${config.title} detail`}>
              <thead>
                <tr>
                  <th>{config.partyLabel}</th>
                  <th>Project</th>
                  {isAR ? <th>Type</th> : null}
                  <th>{isAR ? 'Document #' : 'PO #'}</th>
                  {isAR ? null : <th>Status</th>}
                  <th>Date</th>
                  <th>Due</th>
                  <th className="right">Days Past Due</th>
                  <th className="right">Total</th>
                  <th className="right">Paid</th>
                  <th className="right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => (
                  <tr key={r.id}>
                    <td>{r[config.partyField]}</td>
                    <td>{r.projectName}</td>
                    {isAR ? <td>{r.docType}</td> : null}
                    <td>
                      {(() => {
                        const label = (isAR ? r.docNumber : r.poNumber) || '-';
                        const href = documentHref(r, isAR);
                        return href ? <a href={href}>{label}</a> : label;
                      })()}
                    </td>
                    {isAR ? null : <td>{r.status}</td>}
                    <td>{formatDate(r.date)}</td>
                    <td>{formatDate(r.dueDate)}</td>
                    <td className="right">{r.daysPastDue > 0 ? r.daysPastDue : '-'}</td>
                    <td className="right">{formatCurrency(r.total)}</td>
                    <td className="right">{formatCurrency(r.paid)}</td>
                    <td className="right">
                      <strong>{formatCurrency(r.balance)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
